import { useEffect, useState, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface BuildingLoadingOverlayProps {
  visible: boolean;
  label?: string;
  children: ReactNode;
}

const FADE_OUT_MS = 250;

export function BuildingLoadingOverlay({ visible, label, children }: BuildingLoadingOverlayProps) {
  const [mounted, setMounted] = useState(visible);

  useEffect(() => {
    if (visible) {
      setMounted(true);
      return;
    }
    const timeout = setTimeout(() => setMounted(false), FADE_OUT_MS);
    return () => clearTimeout(timeout);
  }, [visible]);

  // Children mount only after the overlay is fully unmounted (post fade-out),
  // so any animations they kick off — e.g. recharts mount animations — start
  // from 0% with full visibility, instead of running behind the opaque overlay
  // and revealing mid-animation when the fade completes.
  return (
    <div className="relative min-h-screen bg-background">
      {!mounted && children}
      {mounted && (
        <div
          aria-hidden={!visible}
          role="status"
          className={cn(
            "absolute inset-x-0 top-0 z-30 flex h-screen flex-col items-center justify-center gap-3 bg-background",
            visible ? "animate-fade-in" : "animate-fade-out opacity-0"
          )}
        >
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          {label && <p className="text-sm text-muted-foreground">{label}</p>}
        </div>
      )}
    </div>
  );
}

export default BuildingLoadingOverlay;
